'use client';

import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import {
  Background,
  BackgroundVariant,
  type Connection,
  Controls,
  type EdgeChange,
  MiniMap,
  type NodeChange,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import { useCallback, useMemo, useRef } from 'react';
import { LuZap } from 'react-icons/lu';

import { useColorMode } from '@components/ui/color-mode';

import AnchoredPopover from './AnchoredPopover';
import WorkflowEdge from './edges/WorkflowEdge';
import ActionNode from './nodes/ActionNode';
import PaymentLinkNode from './nodes/PaymentLinkNode';
import RoutingNode from './nodes/RoutingNode';
import SupplierPayoutNode from './nodes/SupplierPayoutNode';
import TriggerNode from './nodes/TriggerNode';
import NodeConfigPopover from './NodeConfigPopover';
import NodePickerPopover from './NodePickerPopover';
import {
  ActionType,
  type CatalogItem,
  type PaymentLinkNodeData,
  PickerMode,
  type PickerState,
  TriggerType,
  type WorkflowEdge as WorkflowEdgeType,
  type WorkflowNode,
  WorkflowNodeType,
} from './types';

interface Props {
  nodes: WorkflowNode[];
  edges: WorkflowEdgeType[];
  pickerState: PickerState;
  configNodeId: string | null;
  getNode: (id: string) => WorkflowNode | undefined;
  actions: {
    onNodesChange: (changes: NodeChange[]) => void;
    onEdgesChange: (changes: EdgeChange[]) => void;
    addTriggerNode: (triggerType: TriggerType, position?: { x: number; y: number }) => string;
    addActionNode: (actionType: ActionType, position?: { x: number; y: number }) => string;
    addRoutingNode: (position?: { x: number; y: number }) => string;
    addPaymentLinkNode: (position?: { x: number; y: number }) => string;
    addSupplierPayoutNode: (position?: { x: number; y: number }) => string;
    addSupplierBranch: (routingNodeId: string) => void;
    removeNode: (nodeId: string) => void;
    updateNodeData: (nodeId: string, data: Partial<WorkflowNode['data']>) => void;
    addEdge: (sourceId: string, targetId: string, sourceHandle?: string) => void;
    removeEdge: (edgeId: string) => void;
    openPicker: (state: Omit<PickerState, 'open'>) => void;
    closePicker: () => void;
    openConfig: (nodeId: string) => void;
    closeConfig: () => void;
    replaceNode: (nodeId: string, catalogItemId: string, nodeType: WorkflowNodeType, triggerType?: TriggerType, actionType?: ActionType) => void;
    insertNodeOnEdge: (edgeId: string, nodeId: string) => void;
  };
}

const NODE_TYPES = {
  trigger: TriggerNode,
  action: ActionNode,
  routing: RoutingNode,
  paymentLink: PaymentLinkNode,
  supplierPayout: SupplierPayoutNode,
} as const;

const EDGE_TYPES = {
  workflow: WorkflowEdge,
} as const;

const DEFAULT_EDGE_OPTIONS = { type: 'workflow' } as const;

const EmptyState = ({ onAddTrigger }: { onAddTrigger: () => void }) => (
  <Flex
    position="absolute"
    top="50%"
    left="50%"
    transform="translate(-50%, -50%)"
    direction="column"
    alignItems="center"
    gap={3}
    zIndex={5}
    pointerEvents="auto"
  >
    <Flex
      bg="surface.background"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="xl"
      boxShadow="sm"
      px={6}
      py={5}
      direction="column"
      alignItems="center"
      gap={3}
      cursor="pointer"
      _hover={{ boxShadow: 'md', borderColor: 'border.dark' }}
      onClick={onAddTrigger}
    >
      <Icon boxSize="32px" color="text.placeholder">
        <LuZap />
      </Icon>
      <Text fontSize="sm" fontWeight="medium" color="text.primary">
        Add a trigger to get started
      </Text>
      <Text fontSize="xs" color="text.secondary" textAlign="center" maxW="200px">
        Choose an event that will start this workflow
      </Text>
    </Flex>
  </Flex>
);

const WorkflowCanvas = ({
  nodes,
  edges,
  pickerState,
  configNodeId,
  getNode,
  actions,
}: Props) => {
  const { colorMode } = useColorMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const onConnect = useCallback(
    (connection: Connection) => {
      if (connection.source != null && connection.target != null) {
        actions.addEdge(connection.source, connection.target, connection.sourceHandle ?? undefined);
      }
    },
    [actions],
  );

  const handlePickerSelect = useCallback(
    (item: CatalogItem) => {
      const { mode, anchorNodeId, anchorEdgeId, position } = pickerState;
      let newNodeId: string | undefined;

      if (mode === PickerMode.Replace && anchorNodeId != null) {
        actions.replaceNode(
          anchorNodeId,
          item.id,
          item.nodeType,
          item.triggerType,
          item.actionType,
        );
      } else if (mode === PickerMode.Add) {
        const anchorNode = anchorNodeId != null ? getNode(anchorNodeId) : undefined;
        const anchorIsRouting = anchorNode?.data.nodeType === WorkflowNodeType.Routing;

        // Adding a supplier from a staging node = a ranked branch: this spawns a
        // ranked path, a connected payout node, and the path-handle edge in one go.
        if (anchorIsRouting && anchorNodeId != null && item.nodeType === WorkflowNodeType.SupplierPayout) {
          actions.addSupplierBranch(anchorNodeId);
          actions.closePicker();
          return;
        }

        // A payment link feeds the pool, so it sits to the left and wires INTO the
        // staging node; everything else flows out to the right of its anchor.
        const feedsAnchor = anchorIsRouting && item.nodeType === WorkflowNodeType.PaymentLink;
        const newPos = anchorNode != null
          ? { x: anchorNode.position.x + (feedsAnchor ? -320 : 300), y: anchorNode.position.y }
          : position;

        if (item.nodeType === WorkflowNodeType.Trigger && item.triggerType != null) {
          newNodeId = actions.addTriggerNode(item.triggerType, newPos);
        } else if (item.nodeType === WorkflowNodeType.Action && item.actionType != null) {
          newNodeId = actions.addActionNode(item.actionType, newPos);
        } else if (item.nodeType === WorkflowNodeType.Routing) {
          newNodeId = actions.addRoutingNode(newPos);
        } else if (item.nodeType === WorkflowNodeType.PaymentLink) {
          newNodeId = actions.addPaymentLinkNode(newPos);
        } else if (item.nodeType === WorkflowNodeType.SupplierPayout) {
          newNodeId = actions.addSupplierPayoutNode(newPos);
        }

        if (newNodeId != null && anchorNodeId != null) {
          if (feedsAnchor) {
            actions.addEdge(newNodeId, anchorNodeId);
          } else {
            actions.addEdge(anchorNodeId, newNodeId);
          }
        }
      } else if (mode === PickerMode.InsertOnEdge && anchorEdgeId != null) {
        if (item.nodeType === WorkflowNodeType.Trigger && item.triggerType != null) {
          newNodeId = actions.addTriggerNode(item.triggerType, position);
        } else if (item.nodeType === WorkflowNodeType.Action && item.actionType != null) {
          newNodeId = actions.addActionNode(item.actionType, position);
        } else if (item.nodeType === WorkflowNodeType.Routing) {
          newNodeId = actions.addRoutingNode(position);
        } else if (item.nodeType === WorkflowNodeType.PaymentLink) {
          newNodeId = actions.addPaymentLinkNode(position);
        } else if (item.nodeType === WorkflowNodeType.SupplierPayout) {
          newNodeId = actions.addSupplierPayoutNode(position);
        }
        if (newNodeId != null) {
          actions.insertNodeOnEdge(anchorEdgeId, newNodeId);
        }
      }

      actions.closePicker();
      // A fresh payment link starts empty — open its config so the user can
      // immediately link an existing link or fill out a new one.
      if (newNodeId != null && item.nodeType === WorkflowNodeType.PaymentLink) {
        actions.openConfig(newNodeId);
      }
    },
    [pickerState, actions, getNode],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const bounds = containerRef.current?.getBoundingClientRect();
      if (bounds == null) {return;}
      const flowPos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      actions.openPicker({
        mode: PickerMode.Add,
        position: flowPos,
      });
    },
    [actions, screenToFlowPosition],
  );

  const handlePaneClick = useCallback(() => {
    actions.closePicker();
    actions.closeConfig();
  }, [actions]);

  const handleAddFirstTrigger = useCallback(() => {
    actions.openPicker({ mode: PickerMode.Add, position: { x: 200, y: 200 } });
  }, [actions]);

  const configNode = configNodeId != null ? getNode(configNodeId) : undefined;

  // Payment links already attached to another node — excluded from the
  // "link existing" picker so each link feeds only one staging pool.
  const takenLinkIds = useMemo(
    () =>
      nodes
        .filter(
          (node) =>
            node.id !== configNodeId && node.data.nodeType === WorkflowNodeType.PaymentLink,
        )
        .map((node) => (node.data as PaymentLinkNodeData).linkId)
        .filter((linkId): linkId is string => linkId != null),
    [nodes, configNodeId],
  );

  return (
    <Box ref={containerRef} position="relative" w="full" h="full" onContextMenu={handleContextMenu}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={actions.onNodesChange}
        onEdgesChange={actions.onEdgesChange}
        onConnect={onConnect}
        onPaneClick={handlePaneClick}
        colorMode={colorMode}
        fitView
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
        panOnDrag
        zoomOnScroll
        zoomOnPinch
        nodesDraggable
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls
          showInteractive={false}
          style={{ borderRadius: 8, overflow: 'hidden' }}
        />
        <MiniMap
          style={{ borderRadius: 8, overflow: 'hidden' }}
          maskColor="rgba(0, 0, 0, 0.1)"
        />
      </ReactFlow>
      {nodes.length === 0 && !pickerState.open && (
        <EmptyState onAddTrigger={handleAddFirstTrigger} />
      )}
      {pickerState.open && (
        <AnchoredPopover
          nodeId={pickerState.anchorNodeId}
          flowPosition={pickerState.position}
          containerRef={containerRef}
          placement="right-start"
        >
          <NodePickerPopover
            mode={pickerState.mode}
            onSelect={handlePickerSelect}
            onClose={actions.closePicker}
          />
        </AnchoredPopover>
      )}
      {configNode != null && (
        <AnchoredPopover
          nodeId={configNodeId ?? undefined}
          containerRef={containerRef}
          placement="right-start"
        >
          <NodeConfigPopover
            key={configNode.id}
            nodeId={configNode.id}
            data={configNode.data}
            takenLinkIds={takenLinkIds}
            onUpdate={actions.updateNodeData}
            onClose={actions.closeConfig}
          />
        </AnchoredPopover>
      )}
    </Box>
  );
};

export default WorkflowCanvas;
